import { DateTime, Duration } from 'luxon';
import { Product, ProductInventoryUnit, ProductTrackingType } from '../product/product';
import { User, UserAddress } from '../user/user';
import { PaginationRequest } from '../pagination/pagination';
import { NonePaymentMethod, OrderPayment, PaymentMethod, PaymentMethodRequest } from '../payment/payment';
import { Invoice } from '../invoice/invoice';
import { ShippingType } from '../delivery/delivery';
import { OrderGoogleCalculation } from '../financials/financials';
import { Admin } from '../admin/admin';
import { CustomOrderPriceRequest } from '../pricing/pricing';
import { TaxBreakdown } from '../tax/tax';
import { Representative } from '../representatives/representative';
import { DispatchProgress, ItemDispatchProgress } from '../dispatch/dispatch';

export enum OrderType {
  Sale = 'Sale',
  Rental = 'Rental',
}

export enum OrderStatus {
  Requested = 'Requested',
  Approved = 'Approved',
  Cancelled = 'Cancelled',
  Quote = 'Quote',
  Missed = 'Missed',
}

export enum OrderPaymentMethod {
  CreditCard = 'CreditCard',
  OnAccount = 'OnAccount',
  ProceedWithoutPayment = 'ProceedWithoutPayment',
  Cash = 'Cash',
  Check = 'Check',
  Terminal = 'Terminal',
  BankAccount = 'BankAccount',
  PayLater = 'PayLater',
  Credit = 'Credit',
  Custom = 'Custom',
}

export enum OrderPaymentStatus {
  Pending = 'Pending',
  Paid = 'Paid',
  CashOnDelivery = 'CashOnDelivery',
  Cancelled = 'Cancelled',
}

export enum OrderReturnStatus {
  NotStarted = 'NotStarted',
  OnRent = 'OnRent',
  OffRent = 'OffRent',
  Complete = 'Complete',
}

export class OrderProduct {
  id!: number;
  orderId!: number;
  name!: string;
  tax!: number;
  isTaxFree!: boolean;
  product!: Product;
  assignedUnits!: ProductInventoryUnit[];
  status?: OrderReturnStatus;
  type!: OrderType;
  quantity!: number;
  rentalStartDate!: DateTime;
  rentalEndDate?: DateTime;
  actualRentalStartDate?: DateTime;
  actualRentalEndDate?: DateTime;
  bufferMinutesBeforeRental?: number;
  bufferMinutesAfterRental?: number;
  shippingTypeId?: number;
  shippingType!: ShippingType;
  additionalFees!: OrderProductAdditionalFee[];
  customerBuyingPrice!: number;
  trackingType?: ProductTrackingType;
  discountPercentage!: number;
  options!: OrderProductOption[];
  services!: OrderProductService[];
  inventoryReturnedAt?: DateTime;
  customOrderRentalRates?: CustomOrderPriceRequest[];
  deliveryDriverId?: number;
  pickupDriverId?: number;
  description?: string;
  generalLedgerAccountId?: string;
  taxCategoryId?: string;
  equipmentOwner?: string; // custom rental
  deliveryStatus?: ItemDispatchProgress;
  pickupStatus?: ItemDispatchProgress;
}

export type OrderProductOption = {
  id: number;
  orderProductId: number;
  name: string;
  value: string;
  productOptionId: number;
  productOptionValueId: number;
};

export type OrderProductService = {
  id: number;
  orderProductId: number;
  serviceId: number;
  name: string;
  price: number;
  quantity: number;
  value: string;
};

export class Order {
  id!: number;
  locationId!: number;
  orderNumber!: string;
  status!: OrderStatus;
  quoteExpiresAt?: DateTime;
  returnStatus!: OrderReturnStatus;
  type!: OrderType;
  isDeleted!: boolean;
  products!: OrderProduct[];
  user!: User;
  pickupCharged!: number;
  pickupUpdatedAt?: DateTime;
  shippingCharged?: number;
  shippingUpdatedAt?: DateTime;
  durationDiscount?: number;
  discountTotal?: number;
  damageWaiver?: number;
  applyDamageWaiverTax!: boolean;
  damageWaiverRecurring!: boolean;
  damageWaiverUpdatedAt?: DateTime;
  additionalDiscount?: number;
  billingAddress?: UserAddress;
  shippingAddress?: UserAddress;
  taxPercentage?: number;
  dateAdded!: DateTime;
  source!: OrderSource;
  netCharged!: number;
  payments!: OrderPayment[];
  notes!: string;
  cancellationReason?: string;
  cancellationMessage?: string;
  cancellationDate?: DateTime;
  paymentMethod!: PaymentMethod;
  paymentMethodType!: OrderPaymentMethod;
  paymentType?: string;
  paymentLastFourDigits?: string;
  taxCharged!: number;
  taxUpdatedAt?: DateTime;
  taxPercentageUpdatedAt?: DateTime;
  taxCode?: string;
  poNumber?: string;
  discountCoupon?: string;
  cartTotal!: number;
  additionalFees!: OrderAdditionalFee[];
  internalNotes!: OrderInternalNote[];
  signature?: string;
  signatureDate?: DateTime;
  invoices!: Invoice[];
  isPaid!: boolean;
  rentalSecurityPercentage!: number;
  rentalSecurity!: number;
  isCycleBilled!: boolean;
  isAutoCharged!: boolean;
  cycleBillingTerms?: CycleBillingTerms;
  adminId?: number;
  admin?: Admin;
  statusHistory!: OrderStatusHistory[];
  authorizedRepresentative?: Representative;
  googleShippingCalculation?: {
    type: 'Distance' | 'Duration';
    distance: number;
    duration: string;
  };
  returnDate?: DateTime;
  defaultInvoiceFreeText?: string;
  rentalDisclaimer?: string;
  salesRep?: Admin;
  documentType!: OrderDocumentType;
  taxBreakdown?: TaxBreakdown[];
  dispatches?: Dispatch[];
}

export enum OrderDocumentType {
  Order = 'Order',
  CreditMemo = 'CreditMemo',
}

export enum OrderSource {
  Online = 'Online',
  AdminPhone = 'AdminPhone',
  InStore = 'InStore',
  Quote = 'Quote',
}

export class OrderProductAdditionalFee {
  id!: number;
  orderId!: number;
  productId!: number;
  orderProductId!: number;
  attributeId!: number;
  feeTitle!: string;
  feeType!: string;
  feeValue?: number;
  capValue?: number;
  feeAppliedValue!: number;
  quantity!: number;
  taxApplied!: boolean;
  recurring!: boolean;
  updatedAt?: DateTime;
}
export class OrderAdditionalFee {
  id!: number;
  feeId!: number;
  orderId!: number;
  feeTitle!: string;
  feeType!: string;
  feeValue!: number;
  capValue?: number;
  feeAppliedValue!: number;
  recurring!: boolean;
  taxApplied!: boolean;
  updatedAt?: DateTime;
  optional?: boolean;
}

export class OrderInternalNote {
  id!: number;
  orderId!: number;
  comment?: string;
  author!: Admin;
  createdAt!: DateTime;
}

export type OrderWorkFlowStatus =
  | 'Requests'
  | 'Approved'
  | 'On Rent'
  | 'Off Rent'
  | 'Completed'
  | 'Cancelled'
  | 'Quote'
  | 'Missed';

export class OrderSearchRequest extends PaginationRequest {
  id?: number;
  ids?: number[];
  keyword?: string;
  minPrice?: number;
  maxPrice?: number;
  productIds?: number[];
  locationId?: number;
  type?: OrderType;
  from?: DateTime;
  to?: DateTime;
  startDateFrom?: DateTime;
  startDateTo?: DateTime;
  endDateFrom?: DateTime;
  endDateTo?: DateTime;
  requestedAfter?: DateTime;
  requestedBefore?: DateTime;
  status?: OrderStatus;
  returnStatus?: OrderReturnStatus;
  workFlowStatus?: OrderWorkFlowStatus[];
  paymentStatus?: OrderPaymentStatus;
  paymentMethod?: OrderPaymentMethod;
  customerId?: number;
  addressId?: number;
  shippingType?: ShippingType[];
  sortBy?: 'id' | 'rentalStart' | 'rentalEnd';
  sortDirection?: 'asc' | 'desc';
  isTaxExempted?: boolean;
  createdBy?: number;
  includeOverdue?: boolean;
  documentType?: OrderDocumentType;
}

type CreateCustomAmount = {
  amount: number;
  hasCustomAmount: boolean;
};

export type CreateProductRequest = CreateCustomAmount & {
  isCustom: false;
  uniqueId?: string;
  productId: number;
  delivery: number;
  pickup: number;
  securityDeposit?: number;
  type: OrderType;
  quantity: number;
  additionalFees?: CreateAdditionalFeeRequest[];
  discount?: number;
  assignedUnits?: number[];
  customOrderRentalRates?: CustomOrderPriceRequest[];
  options: {
    id: number;
    name: string;
    valueId: number;
    value: string;
  }[];
  services: {
    id: number;
    name: string;
    value: string;
    price: number;
  }[];
};

export type CreateCustomProductRequest = CreateCustomAmount & {
  isCustom: true;
  uniqueId: string;
  productId: number;
  type: OrderType;
  start?: DateTime;
  end?: DateTime;
  customOrderRentalRates?: CustomOrderPriceRequest[];
  quantity: number;
  taxable: boolean;
  name: string;
  description?: string;
  generalLedgerAccountId?: string;
  deliveryDriverId?: number;
  pickupDriverId?: number;
  taxCategoryId?: string;
  equipmentOwner?: string;
};

type CreateAdditionalFeeRequest = CreateCustomAmount & {
  orderFeeId?: number;
  feeId: number;
};

type CreateCommonRequest = {
  customerId: number;
  locationId: number;
  billingAddressId?: number;
  deliveryAddressId?: number;
  deliveryType: ShippingType;
  notes?: string;
  checkoutBeginDate?: DateTime;
  // Financial data
  currency: Dinero.Currency;
  isCycleBilled: boolean;
  isAutoCharged: boolean;
  products: (CreateProductRequest | CreateCustomProductRequest)[];
  cartTotal: number;
  damageWaiver?: CreateCustomAmount;
  pickup?: CreateCustomAmount;
  delivery?: CreateCustomAmount;
  tax: CreateCustomAmount & {
    percentage: number;
    hasCustomPercentage: boolean;
    taxCode?: string;
    breakdown?: TaxBreakdown[];
  };
  coupon?: {
    code: string;
    amount: number;
    isVip: boolean;
  };
  additionalFees?: CreateAdditionalFeeRequest[];
  additionalDiscount?: number;
  securityDeposit?: {
    amount: number;
    percentage: number;
  };
  total: number;
  googleCalculation?: OrderGoogleCalculation;
  cycleBillingTerms?: CycleBillingTerms;
  authorizedRepresentative?: string;
  dateSigned?: string;
  poNumber?: string;
  salesRepId?: number;
  documentType: OrderDocumentType;
};

export type CreateOrderRequest = CreateCommonRequest & {
  status: OrderStatus;
  source: OrderSource;
  paymentMethod: PaymentMethodRequest;
};

export type CreateQuoteRequest = CreateCommonRequest & {
  status: OrderStatus.Quote;
  source: OrderSource.Quote;
  paymentMethod: NonePaymentMethod;
  expirationDate?: DateTime;
};

export type UpdateOrderFinancialRequest = Omit<
  CreateCommonRequest,
  | 'customerId'
  | 'notes'
  | 'checkoutBeginDate'
  | 'isCycleBilled'
  | 'isAutoCharged'
  | 'cycleBillingTerms'
  | 'authorizedRepresentative'
>;

export type CycleBillingTerms = {
  readonly cycleLength: Duration;
  readonly paymentPeriod: Duration;
  readonly cycleInvoiceDay: number;
  readonly isDayOfMonth: boolean;
};

export type OrderStatusTransitionPaymentRequirement = {
  id: string;
  status: OrderStatus;
  defaultPaymentPercentage?: number;
  requiredPaymentPercentage?: number;
};

export type OrderStatusTransitionPaymentRequirementRequest = Omit<OrderStatusTransitionPaymentRequirement, 'id'>;

export type OrderStatusHistory = {
  orderId: number;
  status: OrderStatus;
  comments: string;
  timestamp: DateTime;
  addedBy?: Admin;
};

export enum OrderMediaType {
  Image = 'Image',
  Video = 'Video',
  Document = 'Document',
}

export type OrderMedia = {
  id: string;
  orderId: number;
  filename: string;
  type: OrderMediaType;
  uploadedBy: number;
  uploadedAt: DateTime;
  downloadUrl?: string;
};

export type OnRentRequest = {
  orderProductIds: number[];
  timestamp: DateTime;
  hourReadings: { unitId: number; hourReading: number }[];
  unitLocationUpdates: number[];
  onRentUpdateDates?: boolean;
};

export type OffRentProduct = {
  productId: number;
  type: ProductTrackingType;
  quantity?: number;
};

export type OffRentRequest = {
  offRentProducts: OffRentProduct[];
  timestamp: DateTime;
};

export type InventoryReturnRequest = {
  orderProductIds: number[];
  returnToLocation: number;
  hourReadings: { unitId: number; hourReading: number }[];
  chargeSecurityDeposit: boolean;
};

export type InternalNotesRequiredValidationError = {
  scope: 'Order';
  errorType: 'InternalNotesRequiredValidationError';
  message: string;
};

export type AgreeToTermsRequiredValidationError = {
  scope: 'Order';
  errorType: 'AgreeToTermsRequiredValidationError';
  message: string;
};

export type QuantityAssignedValidationError = {
  scope: 'OrderProduct';
  errorType: 'QuantityAssignedValidationError';
  orderProductId: number;
  message: string;
};

export type UnitLocationValidationError = {
  scope: 'OrderProduct';
  errorType: 'UnitLocationValidationError';
  orderProductId: number;
  unitId: number;
  message: string;
};

export type UnitStatusValidationError = {
  scope: 'OrderProduct';
  errorType: 'UnitStatusValidationError';
  orderProductId: number;
  unitId: number;
  unitStatus: 'inactive' | 'outage';
  message: string;
};

export type UnitOnRentValidationError = {
  scope: 'OrderProduct';
  errorType: 'UnitOnRentValidationError';
  orderProductId: number;
  unitId: number;
  orderId: number;
  orderNumber: string;
  message: string;
};

export type UnitHardDownValidationError = {
  scope: 'OrderProduct';
  errorType: 'UnitHardDownValidationError';
  orderProductId: number;
  unitId: number;
  workOrderId: string;
  message: string;
};

export type UnitNotInServiceValidationError = {
  scope: 'OrderProduct';
  errorType: 'UnitNotInServiceValidationError';
  orderProductId: number;
  unitId: number;
  message: string;
};

export type UnitSoldValidationError = {
  scope: 'OrderProduct';
  errorType: 'UnitSoldValidationError';
  orderProductId: number;
  unitId: number;
  message: string;
};

export type DispatchStatusValidationError = {
  scope: 'Order';
  errorType: 'DispatchStatusValidationError';
  message: string;
};

export type GenericOrderValidationError = {
  scope: 'Order';
  errorType: 'GenericOrderValidationError';
  message: string;
};

export type CanOnRentValidationError =
  | InternalNotesRequiredValidationError
  | AgreeToTermsRequiredValidationError
  | QuantityAssignedValidationError
  | DispatchStatusValidationError
  | UnitLocationValidationError
  | UnitStatusValidationError
  | UnitOnRentValidationError
  | UnitHardDownValidationError
  | UnitNotInServiceValidationError
  | UnitSoldValidationError;

export type CanOffRentValidationError = DispatchStatusValidationError | GenericOrderValidationError;
export type CanInventoryReturnValidationError = DispatchStatusValidationError | GenericOrderValidationError;

export enum DispatchType {
  Delivery,
  Pickup,
}

export class SearchDispatchRequest extends PaginationRequest {
  date!: DateTime;
  driverIds?: number[];
  keyword?: string;
  locationIds?: number[];
  type?: 'Delivery' | 'Pickup';
  hideQuotes?: boolean;
  orderId?: number;
}

export type Dispatch = {
  orderId: number;
  orderNumber: string;
  startTime: DateTime;
  locationId: number;
  scheduledTime?: DateTime;
  products: { delivery?: OrderProduct[]; pickup?: OrderProduct[] };
  driver?: Admin;
  address?: UserAddress;
  user: User;
  representative?: Representative;
  status?: DispatchProgress;
};

class UpdateDispatchRequest {
  orderId!: number;
  orderProductIds!: {
    delivery?: number[];
    pickup?: number[];
  };
}

export class AssignDriverRequest extends UpdateDispatchRequest {
  driverId?: number;
}

export class UpdateScheduledTimeRequest extends UpdateDispatchRequest {
  scheduledTime?: DateTime;
}

export type SeisCertificate = {
  id: number;
  created_at: number;
  updated_at: number;
  customer_name: string;
  deductible: number;
  certificate_rate: number;
  legacy_certificate_id: string;
  legacy_unit_id: string;
  retail_rental_amount: number;
  start_date: number;
  end_date: number;
  customer_email: string;
  submitted_at: number;
  certificate_number: number;
  customer_address: string;
  customer_phone: string;
  location_number: string;
  location_id: number;
  entity_number: string;
  entity_id: number;
  dealer_stock_number: string;
  manufacturer_serial_number: string;
  unit_id: number;
  main_policy_number: string;
  partner_contract_number: string;
  submitted_by: string;
  submitted_by_user_id: number;
  last_updated_by: string;
  last_updated_by_user_id: number;
  status: 'Approved' | 'Current';
  test_record: boolean;

  product_unit_id?: number;
};

export type SplitOrderProductsProduct = Omit<CreateProductRequest | CreateCustomProductRequest, 'isCustom'> & {
  status?: OrderReturnStatus;
  isCustom: boolean;
  actualRentalStartDate?: DateTime;
  actualRentalEndDate?: DateTime;
  deliveryStatus?: ItemDispatchProgress;
  pickupStatus?: ItemDispatchProgress;
};

export type SplitOrderProductsUpdateRequest = Omit<UpdateOrderFinancialRequest, 'products'> & {
  products: SplitOrderProductsProduct[];
};
